//WMTO-141-Password Protection for EdgePRO Marketing Portal
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { redirectTo, setStatusCode } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getHomePageUrl, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    userName = "userName",
    redirectLink = "redirectLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.userName]: string;
        [fields.redirectLink]: LinkFieldValue;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        isSessionLoaded: Object.keys(session).length > 0,
        signedInUserName: session.isAuthenticated && !session.isGuest ? session.userName : undefined,
        isSigningIn: state.context.isSigningIn,
        homePageUrl: getHomePageUrl(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasShellContext;

export interface CheckAuthStyles {
    overlay?: InjectableCss;
}

export const checkAuthStyles: CheckAuthStyles = {
    overlay: {
        css: css`
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 99999;
            background: #fff;
        `,
    },
};

const styles = checkAuthStyles;
const StyledOverlay = getStyledWrapper("div");

// Redirects users who are not signed in (as the configured user, when set) away from the page this widget is placed on.
const CheckAuth = ({ fields, isSessionLoaded, signedInUserName, isSigningIn, homePageUrl, shellContext }: Props) => {
    const { url: redirectUrl } = useGetLink(fields.redirectLink);
    const targetUrl = redirectUrl || homePageUrl;

    // with a configured user name only that user may view the page, otherwise any signed in user
    const userName = fields.userName?.trim().toLowerCase();
    const isAllowed = !!signedInUserName && (!userName || signedInUserName.toLowerCase() === userName);

    // never redirect content editors out of the CMS
    const shouldRedirect = !shellContext.isInShell && isSessionLoaded && !isSigningIn && !isAllowed;

    // Full page load: redirect on the server so the page is never sent to the browser.
    // The SSR redirect only allows relative urls; absolute ones fall back to the client redirect below.
    if (IS_SERVER_SIDE && shouldRedirect && targetUrl.startsWith("/")) {
        setStatusCode(302);
        redirectTo(targetUrl);
    }

    // Client side navigation (or absolute url): redirect in the browser
    useEffect(() => {
        if (shouldRedirect) {
            window.location.replace(targetUrl);
        }
    }, [shouldRedirect]);

    // hide the page content until the browser redirect happens
    return shouldRedirect ? <StyledOverlay {...styles.overlay} /> : null;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(CheckAuth)),
    definition: {
        group: "Basic",
        icon: "Lock",
        displayName: "Check Auth",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.userName,
                editorTemplate: "TextField",
                displayName: "Allowed User Name / Email (empty = any signed in user)",
                defaultValue: "",
                sortOrder: 0,
            },
            {
                fieldType: "Contextual",
                name: fields.redirectLink,
                editorTemplate: "LinkField",
                displayName: "Redirect If Not Signed In",
                defaultValue: { type: "Page", value: "" },
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
//WMTO-141-Password Protection for EdgePRO Marketing Portal